import { registerActions } from "../actionRegistry";
import { toastSuccess } from "../ui/messages";
import { warnIfPreactManaged } from "./elementIds";
/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
/**
 * DOM Utility Functions
 */

/**
 * Shorthand for getElementById with type safety
 */
export function getDomElement<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/**
 * Like getDomElement, but throws if the element is not found.
 * Use when the element is expected to always exist.
 */
export function getDomElementOrThrow<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id) as T | null;
  if (!el) throw new Error(`Element not found: #${id}`);
  return el;
}

/**
 * Build an element.
 *
 * Prefer this over `setHtml` with a template literal whenever the markup is
 * built and *used* by the same code. Two things follow from returning a node
 * rather than a string:
 *
 *  - **You keep the reference**, so there is no id to mint and look back up.
 *    Most of the id round-trips in this bundle exist only because the builder
 *    threw its element away and had to find it again — several of them then read
 *    `data-*` attributes back off the very element their handler was bound to.
 *  - **Strings become TEXT nodes.** `append("<img>")` renders four visible
 *    characters, so an interpolated filename, artist name or error message
 *    cannot become markup. Escaping stops being something you can forget.
 *
 * `setHtml` is still right for markup that genuinely crosses a seam — a server
 * fragment, or a builder whose output another module locates by id.
 *
 * Set anything else (`id`, `src`, `style`, listeners) on the returned node.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = "",
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (children.length) node.append(...children);
  return node;
}

/**
 * Get the value of an input/select/textarea element by ID.
 * Returns empty string if element not found.
 *
 * The dev-only guard covers the failure `check:dom-ids` cannot see: an id that
 * IS emitted, but by an element with no `.value`. `<pre id="adm_user_password">`
 * read as an input yielded `undefined ?? ""` — indistinguishable from the user
 * leaving the field blank, which is how the add-user modal shipped reporting
 * "Fill in all fields" for a form the user had filled in.
 */
export function getElementValue(id: string): string {
  const el = getDomElement(id);
  if (el && !("value" in el)) {
    if (window.location.hostname === "localhost") {
      console.error(
        `getElementValue("${id}"): <${el.tagName.toLowerCase()}> has no .value — ` +
          `this silently reads "". Use textContent, or point at the form control.`
      );
    }
    return "";
  }
  return (el as HTMLInputElement | null)?.value ?? "";
}

/**
 * Shorthand for document.querySelectorAll
 */
export function queryAll<T extends Element = Element>(selector: string): NodeListOf<T> {
  return document.querySelectorAll<T>(selector);
}

/**
 * Extract the id suffix an element carries after a known DOM-id prefix
 * (the `el.id.replace("prefix_", "")` pattern used all over the legacy UI).
 * Returns "" when the element's id doesn't start with the prefix.
 */
export function idSuffix(el: Element, prefix: string): string {
  return el.id.startsWith(prefix) ? el.id.slice(prefix.length) : "";
}

/**
 * Show an element
 */
export function show(el: HTMLElement | string, display = "block"): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (element) {
    element.style.display = display;
  }
}

/**
 * Hide an element
 */
export function hide(el: HTMLElement | string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (element) {
    element.style.display = "none";
  }
}

/**
 * Set element HTML content
 */
export function setHtml(el: HTMLElement | string, html: string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (element) {
    if (typeof el === "string") warnIfPreactManaged(el, "setHtml");
    element.innerHTML = html;
  }
}

/**
 * Set an element's text content.
 *
 * Prefer this over `setHtml` whenever the value is text — a count, a label, a
 * message, or a clear. It is not a style preference: `innerHTML` parses what you
 * give it, so any `<` or `&` in the value is interpreted as markup, and this
 * bundle has no escaping helper to prevent that. `textContent` cannot be escaped
 * wrong because nothing is parsed.
 *
 * Null-safe in the same way as `setHtml`, matching jQuery's `.html()` on an empty
 * selection: a missing element is a no-op, not a throw.
 */
export function setText(el: HTMLElement | string, text: string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (element) {
    element.textContent = text;
  }
}

/**
 * Insert an HTML string as the element's first child (the `$(el).prepend(html)`
 * equivalent).
 */
export function prependHtml(el: HTMLElement | string, html: string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (typeof el === "string") warnIfPreactManaged(el, "prependHtml");
  element?.insertAdjacentHTML("afterbegin", html);
}

/**
 * Set a single inline style property (the `$(el).css(prop, value)` equivalent).
 * Instant — use animateOpacity() when a transition is wanted.
 * Pass "" to clear the inline value and fall back to the stylesheet.
 */
export function setStyle(el: HTMLElement | string, prop: string, value: string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  element?.style.setProperty(prop, value);
}

/**
 * Add CSS class to element
 */
export function addClass(el: HTMLElement | string, className: string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  element?.classList.add(className);
}

/**
 * Remove CSS class from element
 */
export function removeClass(el: HTMLElement | string, className: string): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  element?.classList.remove(className);
}

/**
 * Check if email is valid
 */
export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

/**
 * Copy text to clipboard and show notification.
 * Delegates to toClipboardAsync (modern API with fallback).
 */
export function toClipboard(str: string): void {
  void toClipboardAsync(str);
  toastSuccess(`<b>${str}</b> was copied to clipboard`);
}

/**
 * Copy text to clipboard (async version, returns success boolean)
 */
async function toClipboardAsync(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    // Deliberate: execCommand is deprecated but is the only clipboard API that
    // works when navigator.clipboard is unavailable (non-secure origin, older
    // browsers) — which is exactly the case this catch block exists for.
    // oxlint-disable-next-line typescript/no-deprecated
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  }
}

// Pending fadeOut hide timers, so a fadeIn during the fade doesn't get
// its element hidden when the earlier fadeOut's timeout fires.
const fadeTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Fade in an element (jQuery-like).
 * Pass display="" to clear an inline display:none and let the
 * stylesheet/default display apply (e.g. inline for <img>).
 */
export function fadeIn(el: HTMLElement | string, duration = 400, display = "block"): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (!element) return;

  const pending = fadeTimers.get(element);
  if (pending !== undefined) {
    clearTimeout(pending);
    fadeTimers.delete(element);
  }

  element.style.opacity = "0";
  element.style.display = display;
  element.style.transition = `opacity ${duration}ms ease`;

  // Trigger reflow
  void element.offsetHeight;

  element.style.opacity = "1";
}

/**
 * Fade out an element (jQuery-like)
 */
export function fadeOut(el: HTMLElement | string, duration = 400): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (!element) return;

  element.style.transition = `opacity ${duration}ms ease`;
  element.style.opacity = "0";

  const pending = fadeTimers.get(element);
  if (pending !== undefined) clearTimeout(pending);
  fadeTimers.set(
    element,
    setTimeout(() => {
      fadeTimers.delete(element);
      element.style.display = "none";
    }, duration)
  );
}

/**
 * Transition an element's opacity without touching display.
 */
export function animateOpacity(el: HTMLElement | string, opacity: number, duration = 400): void {
  const element = typeof el === "string" ? getDomElement(el) : el;
  if (!element) return;

  element.style.transition = `opacity ${duration}ms ease`;
  // Trigger reflow so the transition applies from the current opacity
  void element.offsetHeight;
  element.style.opacity = String(opacity);
}

/**
 * Delegated event listener (jQuery $(root).on(type, selector, handler) equivalent).
 * The handler receives the element matching `selector` (via closest(), so clicks
 * on descendants still match) — the replacement for jQuery's $(this).
 * Note: use "focusout"/"focusin" instead of "blur"/"focus" (those don't bubble).
 */
export function delegate<K extends keyof DocumentEventMap>(
  type: K,
  selector: string,
  handler: (event: DocumentEventMap[K], target: HTMLElement) => void,
  root: Document | HTMLElement = document
): void {
  root.addEventListener(type, (event) => {
    const origin = event.target;
    if (!(origin instanceof Element)) return;
    const target = origin.closest<HTMLElement>(selector);
    if (target) handler(event as DocumentEventMap[K], target);
  });
}

registerActions({
  toclipboard: toClipboard,
});
